(function () {
  'use strict';

  // ─── Menu Data ────────────────────────────────────────────────────────────
  // Fetched from the admin app's /api/menu endpoint on page load. Cached in
  // localStorage so a transient admin outage doesn't blank the menu.

  let MENU = { prixFixe: null, categories: [] };
  const MENU_CACHE_KEY = 'chezg_catering_menu_v1';

  // ─── Bootstrap ───────────────────────────────────────────────────────────

  const root = document.getElementById('holiday-package-catering-root');
  if (!root) return;

  const CONFIG = {
    locale:                    root.dataset.locale || 'en',
    currency:                  root.dataset.currency || 'CAD',
    paypalClientId:            root.dataset.paypalClientId || '',
    emailjsPublicKey:          root.dataset.emailjsPublicKey || '',
    emailjsServiceId:          root.dataset.emailjsServiceId || '',
    emailjsCustomerTemplate:   root.dataset.emailjsCustomerTemplate || '',
    emailjsRestaurantTemplate: root.dataset.emailjsRestaurantTemplate || '',
    adminApiUrl:               root.dataset.adminApiUrl || '',
    adminApiKey:               root.dataset.adminApiKey || '',
  };

  // Accept either a base URL (e.g. https://allspice.chezgreenberg.com) or the
  // full orders endpoint (e.g. https://allspice.chezgreenberg.com/api/orders) —
  // normalise to a base so we can construct both /api/orders and /api/menu.
  const ADMIN_BASE = (CONFIG.adminApiUrl || '')
    .replace(/\/api\/orders\/?$/, '')
    .replace(/\/+$/, '');
  const ADMIN_ORDERS_URL = ADMIN_BASE ? ADMIN_BASE + '/api/orders' : '';
  // Pass through any ?preview=<uuid>,... from the page URL so admins can view
  // inactive pages/packages. Skip caching whenever preview is active.
  let PREVIEW_TOKENS = '';
  try {
    PREVIEW_TOKENS = (new URLSearchParams(window.location.search).get('preview') || '').trim();
  } catch (_) {}
  const PREVIEW_MODE = !!PREVIEW_TOKENS;
  const ADMIN_MENU_URL   = ADMIN_BASE
    ? (ADMIN_BASE + '/api/menu' + (PREVIEW_TOKENS ? '?preview=' + encodeURIComponent(PREVIEW_TOKENS) : ''))
    : '';

  // Translate helper: picks locale key or falls back to en
  const t = (obj) => (obj && typeof obj === 'object') ? (obj[CONFIG.locale] || obj.en) : (obj || '');

  // ─── State ────────────────────────────────────────────────────────────────

  const cart = {}; // { itemId: quantity }
  let paymentMethod = 'in_person'; // 'in_person' | 'online'

  // ─── Google Analytics funnel tracking ──────────────────────────────────────
  // The whole pre-order flow lives on one URL (/pages/pre-order), so GA can't
  // tell the steps apart on its own. To build a "view → add to cart → checkout"
  // funnel we send a virtual page_view for each step with a synthetic path.
  // The initial view (/pages/pre-order) is already covered by the natural page
  // view, so we only fire the cart and checkout steps here. Each step fires at
  // most once per page load. Works with a direct GA4 tag (window.gtag) and/or
  // Google Tag Manager (window.dataLayer); it's a harmless no-op if neither is
  // present, so nothing breaks if GA isn't installed yet.
  const GA_BASE_PATH = '/pages/pre-order';
  const GA_STEPS_FIRED = {};

  function trackFunnelStep(subPath, title) {
    if (GA_STEPS_FIRED[subPath]) return;
    GA_STEPS_FIRED[subPath] = true;

    const path = GA_BASE_PATH + '/' + subPath;
    const location = window.location.origin + path;

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_title: title,
          page_location: location,
          page_path: path, // ignored by GA4, used by GTM/Universal Analytics
        });
      }
    } catch (_) {}

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'virtual_pageview',
        page_title: title,
        page_location: location,
        page_path: path,
      });
    } catch (_) {}
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  function fmt(price) {
    return '$' + price.toFixed(2);
  }

  function getCartItems() {
    const items = [];
    if (MENU.prixFixe) {
      const pfQty = cart[MENU.prixFixe.id] || 0;
      if (pfQty > 0) {
        items.push({ id: MENU.prixFixe.id, name: t(MENU.prixFixe.name), qty: pfQty, price: MENU.prixFixe.price });
      }
    }
    for (const cat of MENU.categories) {
      for (const item of cat.items) {
        const qty = cart[item.id] || 0;
        if (qty > 0) items.push({ id: item.id, name: t(item.name), qty, price: item.price });
      }
    }
    return items;
  }

  function getCartTotal() {
    return getCartItems().reduce((sum, i) => sum + i.qty * i.price, 0);
  }

  const TAX_RATES = { GST: 0.05, QST: 0.09975 };
  const SERVICE_FEE_RATE = 0.03;

  function getTaxes(subtotal) {
    return {
      gst: subtotal * TAX_RATES.GST,
      qst: subtotal * TAX_RATES.QST,
    };
  }

  function getServiceFee() {
    if (paymentMethod !== 'online') return 0;
    const subtotal = getCartTotal();
    const { gst, qst } = getTaxes(subtotal);
    return (subtotal + gst + qst) * SERVICE_FEE_RATE;
  }

  // Pre-fee total — used for "has items" check and as base for service fee
  function getGrandTotal() {
    const subtotal = getCartTotal();
    const { gst, qst } = getTaxes(subtotal);
    return subtotal + gst + qst;
  }

  function isFormValid() {
    const name   = (document.getElementById('co-name')   || {}).value || '';
    const email  = (document.getElementById('co-email')  || {}).value || '';
    const phone  = (document.getElementById('co-phone')  || {}).value || '';
    const pickup = (document.getElementById('co-pickup') || {}).value || '';
    return name.trim() && /\S+@\S+\.\S+/.test(email.trim()) && phone.trim() && pickup;
  }

  // ─── Load menu from admin API ─────────────────────────────────────────────

  // Pickup config from the API, captured once the menu loads so the picker
  // has something to render against.
  let PICKUP_CONFIG = null;

  function applyMenuPayload(payload) {
    // API returns { prix_fixe, categories }. JS uses { prixFixe, categories }.
    MENU = {
      prixFixe: payload && payload.prix_fixe ? payload.prix_fixe : null,
      categories: (payload && payload.categories) || [],
    };
    if (payload && payload.pickup) PICKUP_CONFIG = payload.pickup;
  }

  function showMenuError() {
    const container = document.getElementById('co-menu');
    if (!container) return;
    container.innerHTML =
      '<p style="padding:1rem;text-align:center;color:rgba(var(--color-foreground),0.6);">' +
      (CONFIG.locale === 'fr'
        ? 'Le menu est temporairement indisponible. Veuillez réessayer dans un instant.'
        : 'Menu is temporarily unavailable. Please try again in a moment.') +
      '</p>';
  }

  async function loadMenu() {
    if (!ADMIN_MENU_URL) { showMenuError(); return false; }
    try {
      // Cache-bust via query param rather than `cache: 'no-store'`. Safari/WebKit
      // attaches Cache-Control/Pragma request headers for no-store on cross-origin
      // fetches, forcing a preflight the admin API doesn't allow → the request
      // fails in Safari (works in Chrome). The server already sends
      // `max-age=0, must-revalidate`, so the param just guarantees a fresh copy.
      const url = ADMIN_MENU_URL + (ADMIN_MENU_URL.indexOf('?') === -1 ? '?' : '&') + '_=' + Date.now();
      const res = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const payload = await res.json();
      applyMenuPayload(payload);
      // Never cache preview payloads — they contain unreleased content.
      if (!PREVIEW_MODE) {
        try { localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(payload)); } catch (_) {}
      }
      return true;
    } catch (err) {
      console.warn('Live menu fetch failed; falling back to cache.', err);
      if (!PREVIEW_MODE) {
        try {
          const cached = localStorage.getItem(MENU_CACHE_KEY);
          if (cached) { applyMenuPayload(JSON.parse(cached)); return true; }
        } catch (_) {}
      }
      showMenuError();
      return false;
    }
  }

  // ─── Render menu into #co-menu ────────────────────────────────────────────

  function qtyControlHTML(id) {
    const addLabel    = CONFIG.locale === 'fr' ? 'Ajouter'  : 'Add';
    const removeLabel = CONFIG.locale === 'fr' ? 'Retirer'  : 'Remove';
    return `
      <div class="qty-control" data-qty-item="${id}">
        <button class="qty-btn qty-minus" aria-label="${removeLabel}">−</button>
        <span class="qty-value">0</span>
        <button class="qty-btn qty-plus" aria-label="${addLabel}">+</button>
      </div>`;
  }

  function renderMenu() {
    const container = document.getElementById('co-menu');
    if (!container) return;

    const bestValue = CONFIG.locale === 'fr' ? 'Meilleure valeur' : 'Best Value';
    const alaCarte  = CONFIG.locale === 'fr' ? 'À La Carte' : 'À La Carte';
    const orBuildYourOwn = CONFIG.locale === 'fr'
      ? 'ou composez votre propre sélection ci-dessous'
      : 'or build your own from the menu below';

    let html = '';
    const pf = MENU.prixFixe;
    if (pf) {
      html += `
        <div class="menu-package">
          <div class="menu-package__badge">${bestValue}</div>
          <div class="menu-package__header">
            <span class="menu-package__name">${t(pf.name)}</span>
            <span class="menu-package__price">${fmt(pf.price)}</span>
          </div>
          <div class="menu-package__subtext">${t(pf.subtext)}</div>
          <hr class="menu-package__divider">
          <div class="menu-package__includes">${t(pf.includes)}</div>
          <div class="menu-package__footer">${qtyControlHTML(pf.id)}</div>
        </div>
        <div class="alacarte-title">${alaCarte} <span class="alacarte-sub">${orBuildYourOwn}</span></div>`;
    }

    for (const cat of MENU.categories) {
      html += `<div class="menu-category"><div class="menu-category__title">${t(cat.name)}</div>`;
      for (const item of cat.items) {
        const desc = item.desc ? `<span class="menu-item__desc">${t(item.desc)}</span>` : '';
        html += `
          <div class="menu-item">
            <div class="menu-item__info">
              <span class="menu-item__name">${t(item.name)}</span>
              ${desc}
            </div>
            <div class="menu-item__right">
              <span class="menu-item__price">${fmt(item.price)}</span>
              ${qtyControlHTML(item.id)}
            </div>
          </div>`;
      }
      html += `</div>`;
    }

    container.innerHTML = html;
    attachQtyHandlers();
  }

  // ─── Render order summary ─────────────────────────────────────────────────

  function renderSummary() {
    const listEl  = document.getElementById('co-order-items');
    const totalEl = document.getElementById('co-order-total');
    if (!listEl || !totalEl) return;

    const items      = getCartItems();
    const subtotal   = getCartTotal();
    const { gst, qst } = getTaxes(subtotal);
    const serviceFee = getServiceFee();
    const final      = subtotal + gst + qst + serviceFee;
    const emptyMsg   = CONFIG.locale === 'fr' ? 'Aucun article sélectionné' : 'No items selected';

    listEl.innerHTML = items.length === 0
      ? `<p class="order-empty">${emptyMsg}</p>`
      : items.map(i => `
          <div class="order-line">
            <span>${i.qty} × ${i.name}</span>
            <span>${fmt(i.qty * i.price)}</span>
          </div>`).join('');

    const set = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
    set('co-subtotal',    fmt(subtotal));
    set('co-gst',         fmt(gst));
    set('co-qst',         fmt(qst));
    set('co-service-fee', fmt(serviceFee));
    set('co-order-total', fmt(final));
    set('co-sticky-total', fmt(final));

    // Show service fee row only for online payment
    const feeRow = document.getElementById('co-service-fee-row');
    if (feeRow) feeRow.style.display = serviceFee > 0 ? 'flex' : 'none';

    updatePaymentZone();
  }

  // ─── Payment zone ─────────────────────────────────────────────────────────

  function updatePaymentZone() {
    const hasItems  = getGrandTotal() > 0;
    const formReady = isFormValid();
    const ready     = hasItems && formReady;

    // Continue button in sidebar
    const continueBtn      = document.getElementById('co-continue-btn');
    const continueDisabled = document.getElementById('co-continue-disabled');
    if (continueBtn)      continueBtn.style.display      = hasItems ? 'block' : 'none';
    if (continueDisabled) continueDisabled.style.display = hasItems ? 'none'  : 'block';

    // Mobile sticky bar
    const stickyContinue = document.getElementById('co-sticky-continue');
    const stickyDisabled = document.getElementById('co-sticky-disabled');
    if (stickyContinue) stickyContinue.style.display = hasItems ? 'block' : 'none';
    if (stickyDisabled) stickyDisabled.style.display = hasItems ? 'none'  : 'block';

    // Payment zone elements
    const disabledEl        = document.getElementById('co-paypal-disabled');
    const inpersonContainer = document.getElementById('co-inperson-container');
    const paypalContainer   = document.getElementById('co-paypal-container');

    if (!ready) {
      if (disabledEl)        disabledEl.style.display        = 'block';
      if (inpersonContainer) inpersonContainer.style.display = 'none';
      if (paypalContainer)   paypalContainer.style.display   = 'none';
    } else if (paymentMethod === 'online') {
      if (disabledEl)        disabledEl.style.display        = 'none';
      if (inpersonContainer) inpersonContainer.style.display = 'none';
      if (paypalContainer)   paypalContainer.style.display   = 'block';
    } else {
      // in_person
      if (disabledEl)        disabledEl.style.display        = 'none';
      if (paypalContainer)   paypalContainer.style.display   = 'none';
      if (inpersonContainer) inpersonContainer.style.display = 'block';
    }
  }

  // ─── PayPal ───────────────────────────────────────────────────────────────

  function initPayPal() {
    if (!window.paypal || !CONFIG.paypalClientId) return;

    paypal.Buttons({
      style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' },

      onClick: function (data, actions) {
        if (!isFormValid() || getGrandTotal() === 0) {
          highlightErrors();
          return actions.reject();
        }
        return actions.resolve();
      },

      createOrder: function (data, actions) {
        const items      = getCartItems();
        const subtotal   = getCartTotal();
        const { gst, qst } = getTaxes(subtotal);
        const preFee     = subtotal + gst + qst;
        const serviceFee = preFee * SERVICE_FEE_RATE;
        const final      = preFee + serviceFee;
        const desc       = CONFIG.locale === 'fr'
          ? 'Commande traiteur – Chez Greenberg'
          : 'Catering Order – Chez Greenberg';

        return actions.order.create({
          purchase_units: [{
            description: desc,
            amount: {
              currency_code: CONFIG.currency,
              value: final.toFixed(2),
              breakdown: {
                item_total: { currency_code: CONFIG.currency, value: subtotal.toFixed(2) },
                tax_total:  { currency_code: CONFIG.currency, value: (gst + qst).toFixed(2) },
                handling:   { currency_code: CONFIG.currency, value: serviceFee.toFixed(2) },
              },
            },
            items: items.map(i => ({
              name:        i.name.substring(0, 127),
              quantity:    i.qty.toString(),
              unit_amount: { currency_code: CONFIG.currency, value: i.price.toFixed(2) },
            })),
          }],
        });
      },

      onApprove: function (data, actions) {
        return actions.order.capture().then(function (details) {
          const orderId = generateOrderId();
          sendEmails({ txnId: details.id, paymentStatus: 'online', orderId });
          submitOrderToAdmin({ txnId: details.id, paymentStatus: 'online', orderId });
          showSuccess({ txnId: details.id, paymentStatus: 'online', orderId });
        });
      },

      onError: function (err) {
        console.error('PayPal error:', err);
        const msg = CONFIG.locale === 'fr'
          ? 'Une erreur est survenue. Veuillez réessayer.'
          : 'An error occurred. Please try again.';
        alert(msg);
      },
    }).render('#co-paypal-buttons');
  }

  // ─── EmailJS ──────────────────────────────────────────────────────────────

  function sendEmails({ txnId = '—', paymentStatus = 'in_person', orderId = '' }) {
    if (!window.emailjs || !CONFIG.emailjsPublicKey || !CONFIG.emailjsServiceId) return;

    emailjs.init(CONFIG.emailjsPublicKey);

    const name   = document.getElementById('co-name').value.trim();
    const email  = document.getElementById('co-email').value.trim();
    const phone  = document.getElementById('co-phone').value.trim();
    const pickup = document.getElementById('co-pickup').value;
    const notes  = (document.getElementById('co-notes').value || '').trim();
    const items  = getCartItems();

    const pickupFormatted = new Date(pickup).toLocaleString(
      CONFIG.locale === 'fr' ? 'fr-CA' : 'en-CA',
      { dateStyle: 'full', timeStyle: 'short' }
    );

    const subtotal   = getCartTotal();
    const { gst, qst } = getTaxes(subtotal);
    const preFee     = subtotal + gst + qst;
    const serviceFee = paymentStatus === 'online' ? preFee * SERVICE_FEE_RATE : 0;
    const final      = preFee + serviceFee;

    const orderLines = items.map(i => `${i.qty}x ${i.name} — ${fmt(i.qty * i.price)}`).join('\n');

    const paymentLabel = paymentStatus === 'online'
      ? (CONFIG.locale === 'fr' ? 'Payé en ligne (PayPal)' : 'Paid online (PayPal)')
      : (CONFIG.locale === 'fr' ? 'Paiement à la livraison' : 'Pay on pickup');

    const sharedFields = {
      order_id:          orderId,
      order_subtotal:    fmt(subtotal),
      order_gst:         fmt(gst),
      order_qst:         fmt(qst),
      order_service_fee: serviceFee > 0 ? fmt(serviceFee) : '—',
      order_total:       fmt(final),
      payment_status:    paymentLabel,
      transaction_id:    paymentStatus === 'online' ? txnId : '—',
    };

    const logEmailError = (label, err) => {
      console.error(`EmailJS ${label} send failed — status: ${err.status}, text: ${err.text}`, err);
    };

    // — Order Received notice to customer —
    if (CONFIG.emailjsCustomerTemplate) {
      emailjs.send(CONFIG.emailjsServiceId, CONFIG.emailjsCustomerTemplate, {
        to_name:         name,
        to_email:        email,
        order_items:     orderLines,
        pickup_datetime: pickupFormatted,
        notes:           notes || '—',
        ...sharedFields,
      }).catch(err => logEmailError('customer', err));
    }

    // — Notification to restaurant —
    if (CONFIG.emailjsRestaurantTemplate) {
      emailjs.send(CONFIG.emailjsServiceId, CONFIG.emailjsRestaurantTemplate, {
        customer_name:   name,
        customer_email:  email,
        customer_phone:  phone,
        order_items:     orderLines,
        pickup_datetime: pickupFormatted,
        notes:           notes || '—',
        ...sharedFields,
      }).catch(err => logEmailError('restaurant', err));
    }
  }

  // ─── Admin API ───────────────────────────────────────────────────────────

  function submitOrderToAdmin({ txnId = '', paymentStatus = 'in_person', orderId = '' }) {
    if (!ADMIN_ORDERS_URL || !CONFIG.adminApiKey) return;

    const name   = document.getElementById('co-name').value.trim();
    const email  = document.getElementById('co-email').value.trim();
    const phone  = document.getElementById('co-phone').value.trim();
    const pickup = document.getElementById('co-pickup').value;
    const notes  = (document.getElementById('co-notes').value || '').trim();
    const items  = getCartItems();

    const subtotal   = getCartTotal();
    const { gst, qst } = getTaxes(subtotal);
    const preFee     = subtotal + gst + qst;
    const serviceFee = paymentStatus === 'online' ? preFee * SERVICE_FEE_RATE : 0;
    const total      = preFee + serviceFee;

    fetch(ADMIN_ORDERS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': CONFIG.adminApiKey,
      },
      body: JSON.stringify({
        customer_name:  name,
        customer_email: email,
        customer_phone: phone,
        pickup_datetime: pickup,
        notes: notes,
        payment_method: paymentStatus === 'online' ? 'online' : 'in_person',
        payment_status: paymentStatus === 'online' ? 'paid' : 'pending',
        paypal_transaction_id: paymentStatus === 'online' ? txnId : null,
        subtotal: subtotal,
        gst: gst,
        qst: qst,
        service_fee: serviceFee,
        total: total,
        locale: CONFIG.locale,
        items: items.map(i => ({
          item_id:    i.id,
          item_name:  i.name,
          quantity:   i.qty,
          unit_price: i.price,
          line_total: i.qty * i.price,
        })),
      }),
    }).catch(err => console.error('Admin API submit failed:', err));
  }

  // ─── Success screen ───────────────────────────────────────────────────────

  function showSuccess({ txnId = '—', paymentStatus = 'in_person', orderId = '' }) {
    const bodyEl    = root.querySelector('.catering-order__body');
    const successEl = document.getElementById('co-success');
    if (!bodyEl || !successEl) return;

    const name   = document.getElementById('co-name').value.trim();
    const email  = document.getElementById('co-email').value.trim();
    const pickup = document.getElementById('co-pickup').value;
    const items  = getCartItems();

    const subtotal   = getCartTotal();
    const { gst, qst } = getTaxes(subtotal);
    const preFee     = subtotal + gst + qst;
    const serviceFee = paymentStatus === 'online' ? preFee * SERVICE_FEE_RATE : 0;
    const final      = preFee + serviceFee;

    const pickupFormatted = new Date(pickup).toLocaleString(
      CONFIG.locale === 'fr' ? 'fr-CA' : 'en-CA',
      { dateStyle: 'full', timeStyle: 'short' }
    );

    const subtotalLabel = CONFIG.locale === 'fr' ? 'Sous-total' : 'Subtotal';
    const feeLabelText  = CONFIG.locale === 'fr' ? 'Frais de service (3%)' : 'Service Fee (3%)';
    const paymentLabel  = paymentStatus === 'online'
      ? (CONFIG.locale === 'fr' ? 'Payé en ligne (PayPal)' : 'Paid online (PayPal)')
      : (CONFIG.locale === 'fr' ? 'Paiement à la livraison' : 'Pay on pickup');

    document.getElementById('co-success-name').textContent   = name;
    document.getElementById('co-success-email').textContent  = email;
    document.getElementById('co-success-pickup').textContent = pickupFormatted;
    document.getElementById('co-success-total').textContent  = fmt(final);

    const orderIdEl = document.getElementById('co-success-order-id');
    if (orderIdEl) orderIdEl.textContent = orderId;

    const statusEl = document.getElementById('co-success-payment-status');
    if (statusEl) statusEl.textContent = paymentLabel;

    // PayPal transaction ID — only show for online payments
    const txnRowEl = document.getElementById('co-success-txn-row');
    const txnEl    = document.getElementById('co-success-txn');
    if (txnRowEl) txnRowEl.style.display = paymentStatus === 'online' ? 'block' : 'none';
    if (txnEl)    txnEl.textContent = txnId;

    const feeLines = serviceFee > 0 ? [
      `<div class="order-line summary-tax-line"><span>${feeLabelText}</span><span>${fmt(serviceFee)}</span></div>`,
    ] : [];

    document.getElementById('co-success-items').innerHTML = [
      ...items.map(i => `
        <div class="order-line">
          <span>${i.qty} × ${i.name}</span>
          <span>${fmt(i.qty * i.price)}</span>
        </div>`),
      `<hr class="summary-divider">`,
      `<div class="order-line summary-tax-line"><span>${subtotalLabel}</span><span>${fmt(subtotal)}</span></div>`,
      `<div class="order-line summary-tax-line"><span>GST (5%)</span><span>${fmt(gst)}</span></div>`,
      `<div class="order-line summary-tax-line"><span>QST (9.975%)</span><span>${fmt(qst)}</span></div>`,
      ...feeLines,
    ].join('');

    bodyEl.style.display = 'none';
    successEl.classList.add('visible');
    successEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const stickyBar = document.getElementById('co-sticky-bar');
    if (stickyBar) stickyBar.style.display = 'none';
  }

  // ─── Order ID ─────────────────────────────────────────────────────────────

  function generateOrderId() {
    const now  = new Date();
    const date = now.getFullYear().toString()
      + String(now.getMonth() + 1).padStart(2, '0')
      + String(now.getDate()).padStart(2, '0');
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `CG-${date}-${rand}`;
  }

  // ─── Form error highlight ─────────────────────────────────────────────────

  function highlightErrors() {
    ['co-name', 'co-email', 'co-phone', 'co-pickup'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      if (!el.value.trim()) {
        el.classList.add('error');
      } else {
        el.classList.remove('error');
      }
    });
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  function attachQtyHandlers() {
    document.querySelectorAll('[data-qty-item]').forEach(control => {
      const itemId  = control.dataset.qtyItem;
      const valueEl = control.querySelector('.qty-value');
      const minus   = control.querySelector('.qty-minus');
      const plus    = control.querySelector('.qty-plus');

      minus.addEventListener('click', () => {
        if ((cart[itemId] || 0) > 0) {
          cart[itemId]--;
          valueEl.textContent = cart[itemId];
          renderSummary();
        }
      });

      plus.addEventListener('click', () => {
        cart[itemId] = (cart[itemId] || 0) + 1;
        valueEl.textContent = cart[itemId];
        renderSummary();
        trackFunnelStep('cart', 'Pre-Order — Cart'); // GA funnel: first add to cart
      });
    });
  }

  function attachFormHandlers() {
    function scrollToCheckout() {
      const summaryEl = root.querySelector('.catering-order__summary');
      if (!summaryEl) return;
      trackFunnelStep('checkout', 'Pre-Order — Checkout'); // GA funnel: reached checkout
      const headerEl = document.querySelector('.shopify-section-header');
      const headerHeight = headerEl ? headerEl.offsetHeight : 0;
      const top = summaryEl.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
      setTimeout(() => {
        const first = ['co-name', 'co-email', 'co-phone', 'co-pickup']
          .map(id => document.getElementById(id))
          .find(el => el && !el.value.trim());
        if (first) first.focus();
      }, 600);
    }

    const continueBtn = document.getElementById('co-continue-btn');
    if (continueBtn) continueBtn.addEventListener('click', scrollToCheckout);

    const stickyContinueBtn = document.getElementById('co-sticky-continue');
    if (stickyContinueBtn) stickyContinueBtn.addEventListener('click', scrollToCheckout);

    ['co-name', 'co-email', 'co-phone', 'co-pickup'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', () => {
        el.classList.remove('error');
        updatePaymentZone();
      });
    });

    // Auto-format phone as NXX-NXX-XXXX. Autofill hands us the country code
    // ("+1 514-555-1234"), so drop a leading North American 1 before taking
    // ten digits — otherwise the 1 occupies a slot and the last digit falls
    // off the end.
    const phoneEl = document.getElementById('co-phone');
    if (phoneEl) {
      const formatPhone = (el) => {
        let v = el.value.replace(/\D/g, '');
        if (v.length > 10 && v.charAt(0) === '1') v = v.slice(1);
        v = v.slice(0, 10);
        if (v.length <= 3)       el.value = v;
        else if (v.length <= 6)  el.value = v.slice(0, 3) + '-' + v.slice(3);
        else                     el.value = v.slice(0, 3) + '-' + v.slice(3, 6) + '-' + v.slice(6);
      };
      phoneEl.addEventListener('input', function () { formatPhone(this); });
      // Some browsers fill the field without firing 'input'.
      phoneEl.addEventListener('change', function () { formatPhone(this); });
    }
  }

  function attachPaymentMethodHandlers() {
    document.querySelectorAll('input[name="co-payment-method"]').forEach(radio => {
      radio.addEventListener('change', () => {
        paymentMethod = radio.value;
        renderSummary(); // updates service fee row, totals, and payment zone
      });
    });

    const placeOrderBtn = document.getElementById('co-place-order-btn');
    if (placeOrderBtn) {
      placeOrderBtn.addEventListener('click', () => {
        if (!isFormValid() || getGrandTotal() === 0) {
          highlightErrors();
          return;
        }
        placeOrderBtn.disabled = true;
        placeOrderBtn.textContent = CONFIG.locale === 'fr' ? 'Envoi en cours…' : 'Submitting…';
        const orderId = generateOrderId();
        sendEmails({ txnId: '—', paymentStatus: 'in_person', orderId });
        submitOrderToAdmin({ txnId: '', paymentStatus: 'in_person', orderId });
        showSuccess({ txnId: '—', paymentStatus: 'in_person', orderId });
      });
    }
  }

  // ─── Order cutoff & pickup window ────────────────────────────────────────

  const ORDER_CUTOFF = new Date('2026-03-30T23:00:00Z'); // order by March 30 at 7pm EDT

  // Pickup slots come from the admin-configured windows now (falling back to
  // operating_hours); the calendar picker renders them. We still close the
  // page after ORDER_CUTOFF for the passover flow.
  function checkCutoff() {
    const hasLateAccess = new URLSearchParams(window.location.search).get('access') === 'passover2026';
    if (!hasLateAccess && new Date() > ORDER_CUTOFF) {
      const bodyEl   = root.querySelector('.catering-order__body');
      const closedEl = document.getElementById('co-orders-closed');
      if (bodyEl)   bodyEl.style.display   = 'none';
      if (closedEl) closedEl.style.display = 'block';
    }
  }

  function mountPickupPicker() {
    const input = document.getElementById('co-pickup');
    if (!input || !window.ChezGPickupPicker) return;
    const pickup = PICKUP_CONFIG || {
      windows: [],
      operating_hours: [],
      buffer_hours: 48,
      start_date: null,
      end_date: null,
    };
    window.ChezGPickupPicker.mount({
      input,
      locale: CONFIG.locale,
      pickup,
    });
  }

  // ─── Wait for PayPal SDK ──────────────────────────────────────────────────

  function waitForPayPal(attempts) {
    if (window.paypal) { initPayPal(); return; }
    if (attempts <= 0) return;
    setTimeout(() => waitForPayPal(attempts - 1), 300);
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', async function () {
    checkCutoff();
    const menuLoaded = await loadMenu();
    if (menuLoaded) renderMenu();
    mountPickupPicker();
    renderSummary();
    attachFormHandlers();
    attachPaymentMethodHandlers();
    waitForPayPal(20); // try for ~6 seconds

    // Hide the mobile sticky bar when the catering section is fully scrolled past
    const stickyBar = document.getElementById('co-sticky-bar');
    // Move sticky bar to <body> so position:fixed is always relative to the viewport,
    // regardless of any ancestor transforms applied by the theme (which would otherwise
    // shift the fixed containing block away from the viewport).
    if (stickyBar) {
      document.body.appendChild(stickyBar);
    }
    if (stickyBar && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (stickyBar.style.display !== 'none') {
              stickyBar.style.visibility = entry.isIntersecting ? '' : 'hidden';
              stickyBar.style.pointerEvents = entry.isIntersecting ? '' : 'none';
            }
          });
        },
        { threshold: 0 }
      );
      observer.observe(root);
    }
  });
})();
